import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';
import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { AccessRoleDTO, AccessRoleFilterDTO, CompanyDTO, CompanyFilterDTO, UserGroupDTO, UserGroupFilterDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField } from '../../../shared/components';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { UserGroupMaintenance } from './UserGroupMaintenance';
import { UserGroupUserAssociate } from './UserGroupUserAssociate';
import { ListId, LocalStorange } from '../../../shared/environment';
import { useTranslation } from 'react-i18next';

export const UserGroup: React.FC = () => {

    const [companyList, setCompanyList] = useState<CompanyDTO[]>([]);
    const [userGroupList, setUserGroupList] = useState<UserGroupDTO[]>([]);
    const [userGroupFilterDTO, setUserGroupFilterDTO] = useState<UserGroupFilterDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [expadedData, setExpandedData] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);
    const [selectRecord, setSelectRecord] = useState('');

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');
    const { t } = useTranslation();

    useEffect(() => {
        setUserGroupFilterDTO({
            ...userGroupFilterDTO,
            companyId: '',
            name: ''
        } as UserGroupFilterDTO);
    }, []);

    useEffect(() => {
        handleCompanyGetAll();
        handleUserGroupGetAllViewAsync();

        handleGetPermission(ListId.authsphere.userGroupId, setAccessRolePermissionDTO);

    }, [userGroupFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleUserGroupGetAllViewAsync();
    }, [actionExecuted]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync',accessRoleFilterDTO, setActionExecuted, setLoading, undefined,  setList);
    }, []);

    const handleCompanyGetAll = useCallback(async () => {
        const companyFIlterDTO: CompanyFilterDTO = {
            name: '',
            businessRelationshipTypeId: '',
            statusId: '',
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.company', companyFIlterDTO, setLoading, setCompanyList, showMessage);
    }, []);

    const handleUserGroupGetAllViewAsync = useCallback(async () => {
        if (userGroupFilterDTO === undefined)
            return;
        if(accessRolePermissionDTO?.allowGetAllView == undefined)
            return;
        if(!accessRolePermissionDTO?.allowGetAllView) {
            showMessage('userGroup.message.handleUserGroupGetAllViewAsync', 'info');
            return;
        }
        GenericService.GetAllViewAsync1('authsphere.userGroup', userGroupFilterDTO, setActionExecuted, setLoading, setUserGroupList, showMessage, true);
    }, [userGroupFilterDTO, accessRolePermissionDTO]);

    const handleClearFilterClick = () => {
        setUserGroupFilterDTO({
            ...userGroupFilterDTO,
            companyId: '',
            name: ''
        } as UserGroupFilterDTO);
    };

    function handleSearchClick() {
        handleUserGroupGetAllViewAsync();
    }

    const handleNew = () => {
        if(!accessRolePermissionDTO?.allowAdd) {
            showMessage(t('userGroup.message.handleNew'), 'info');
            return;
        }
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        if(!accessRolePermissionDTO?.allowGetById) {
            showMessage(t('userGroup.message.handleViewDetatils'), 'info');
        }
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        if(!accessRolePermissionDTO?.allowDelete) {
            showMessage(t('userGroup.message.handleDelete'), 'info');
            return;
        }
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleAssociateUserGroups = (data: string) => {
        setNewRecord('');
        setDeleteRecord('');
        setEditRecord('');
        setSelectRecord(data);
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: t('userGroup.toolBarActions.newUserGroup'), icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: t('userGroup.cardActions.viewDetails'), icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: t('userGroup.cardActions.delete'), icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
        { id: 'associateUserGroups', toolTip: t('userGroup.cardActions.associateUser'), icon: 'group_outlined', iconColor: 'info', setAction: handleAssociateUserGroups },
    ];

    const chipFilters: IChipFilter[] = [
        { label: t('userGroup.chipFilters.totalRecords'), value: userGroupList.length },
        { label: t('userGroup.chipFilters.name'), value: userGroupFilterDTO?.name },
    ];

    const columns: ICardColumns[] = [
        { field: 'name', label: t('userGroup.columns.name'), size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'description', label: t('userGroup.columns.description'), size: 6, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'company', label: t('userGroup.columns.company'), size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='CANDIDATE'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='name'
                                label={t('userGroup.layoutDashboard.textFieldInput.label')}
                                setValue={setUserGroupFilterDTO}
                                value={userGroupFilterDTO?.name}
                                obj={userGroupFilterDTO}
                                placeholder={t('userGroup.layoutDashboard.textFieldInput.label')}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='companyId'
                                label={t('userGroup.layoutDashboard.selectField.company')}
                                setValue={setUserGroupFilterDTO}
                                value={userGroupFilterDTO?.companyId}
                                obj={userGroupFilterDTO}
                                valueList={companyList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <Box sx={{ display: 'flex', maxHeight: '79vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                    <CardList
                        columns={columns}
                        rows={userGroupList}
                        actions={cardActions}
                        hasChildren={false}
                        setRowExpanded={() => void undefined}
                        cardSize={12}
                    />
                </Box>
                {newRecord && (
                    <UserGroupMaintenance
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {editRecord && (
                    <UserGroupMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (
                    <Delete
                        entity={'authsphere.userGroup'}
                        data={deleteRecord}
                        setIsOpenDiaglog={setIsOpenMaintenance}
                        isOpenDialog={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {selectRecord && (
                    <UserGroupUserAssociate
                        data={selectRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                        actionExecuted={actionExecuted}
                    />
                )}
            </LayoutDashboard>
        </React.Fragment >
    );
};
